/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { capitalize } from '@veeam-vspc/shared/helpers';
import { colors, useGlobalLang, useGlobalAppData } from '@veeam-vspc/shared/components';
import { observer } from 'mobx-react-lite';
import { TimeUnitsInMs } from '@veeam-vspc/shared/core';
import { useLocation } from 'react-router-dom';
import { ReportIntervalRepresentation } from '@veeam-vspc/models/web-controllers';

import type { PieChartModel } from '@veeam-vspc/models/web-controllers';
import type { VspcLang } from 'configs/languages';

import { BillingWidget } from '../../../BillingWidget';
import { HighchartsWrapper } from 'views/components/HighchartsWrapper';
import { IntervalCombobox } from '../IntervalCombobox';
import { IntervalContext } from '../IntervalCombobox/contexts';
import { IntervalStore } from '../IntervalCombobox/stores';
import { getPieOptions } from 'views/components/HighchartsWrapper/helpers';
import { transformPieData } from 'views/components/ChartWidget/helpers';
import { useIntervalRequest, useLocalStorageValue } from 'views/hooks';
import { useIntervalStore } from '../IntervalCombobox/hooks';

import type { PieData } from 'views/components/HighchartsWrapper/interfaces';

const RevenueByServiceTypeWidget = observer(() => {
    const abortController = useRef(new AbortController());
    const didMount = useRef(false);
    const lang = useGlobalLang<VspcLang>();
    const { portalUser } = useGlobalAppData();
    const store = useIntervalStore();
    const { interval } = store;

    const request = useIntervalRequest<{}, PieChartModel>(
        '/Billing/GetBillingSummaryRevenue',
        { interval },
        {
            requestInit: {
                signal: abortController.current.signal,
            },
        },
        5 * TimeUnitsInMs.Minute,
    );

    const { empty, series } = request.data
        ? transformPieData(request.data as PieChartModel)
        : {} as PieData;

    const params = {
        colors: [colors.B400, colors.Grn200, colors.B250, colors.T500, '#834799', colors.Y200, '#e0aa38', colors.R800],
    };

    const options = getPieOptions({
        lang,
        categories: [],
        height: 306,
        series,
        size: 196,
        ...params,
    });

    const title = {
        text: capitalize(lang.REVENUE_BY_SERVICE_TYPE),
        tools: <IntervalCombobox />,
    };

    useEffect(() => {
        if (didMount.current) {
            abortController.current.abort();
            abortController.current = new AbortController();

            return request.forceRequest();
        }

        didMount.current = true;
    }, [interval]);

    const href = portalUser.isPortalOrSiteAdmin() || portalUser.isPortalOperator() || portalUser.isResellerPortal()
        ? '/home/invoices/configurations'
        : null;

    const emptyDesc = {
        href,
        linkText: lang.GENERATE_INVOICE,
        text: lang.GENERATE_INVOICE_TO_VIEW_THE_DATA,
    };

    return (
        <BillingWidget title={title}>
            <HighchartsWrapper
                empty={empty}
                emptyDesc={emptyDesc}
                loading={request.loading}
                options={options}
            />
        </BillingWidget>
    );
});

export const RevenueByServiceType: React.FC = () => {
    const { pathname } = useLocation();
    const stateId = `${pathname}.RevenueByServiceType`;
    const initialInterval = useLocalStorageValue<ReportIntervalRepresentation>(stateId, ReportIntervalRepresentation.LastMonth);

    const store = useMemo(() => new IntervalStore(initialInterval, stateId), [initialInterval, stateId]);

    return (
        <IntervalContext.Provider value={store}>
            <RevenueByServiceTypeWidget />
        </IntervalContext.Provider>
    );
};
